export const invoiceAmounts = (order) => {
  const legacy = order.baseAmount == null || order.gstAmount == null;
  const baseAmount = Number(order.baseAmount ?? order.items?.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0) ?? order.totalAmount);
  const discountAmount = Number(order.discountAmount || 0);
  const taxableAmount = Number(order.taxableAmount ?? Math.max(baseAmount - discountAmount, 0));
  const gstAmount = Number(order.gstAmount || 0);
  return {
    legacy,
    baseAmount,
    discountPercent: Number(order.discountPercent || 0),
    discountAmount,
    taxableAmount,
    gstPercent: Number(order.gstPercent || 0),
    gstAmount,
    totalAmount: Number(order.totalAmount ?? taxableAmount + gstAmount),
  };
};

const money = (value) => `INR ${Number(value || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

export async function downloadDistributorInvoice(order) {
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const width = pdf.internal.pageSize.getWidth();
  const height = pdf.internal.pageSize.getHeight();
  const margin = 16;
  const isShopOrder = order.orderType === "shop" || Boolean(order.shop);
  const buyer = isShopOrder ? order.shop || {} : order.distributor || {};
  const amounts = invoiceAmounts(order);
  let y = 18;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(19);
  pdf.text("SHAHA GROUP", margin, y);
  pdf.setFontSize(12);
  pdf.text("ORDER INVOICE", width - margin, y, { align: "right" });
  y += 7;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.text(isShopOrder ? "Shop supply order" : "Distributor supply order", margin, y);
  pdf.text(`Invoice / Order: ${order.orderNumber}`, width - margin, y, { align: "right" });
  y += 5;
  pdf.text(`Date: ${new Date(order.createdAt).toLocaleDateString("en-IN")}`, width - margin, y, { align: "right" });
  y += 8;
  pdf.setDrawColor(190);
  pdf.line(margin, y, width - margin, y);
  y += 7;

  pdf.setFont("helvetica", "bold");
  pdf.text("BILL TO", margin, y);
  y += 5;
  pdf.setFont("helvetica", "normal");
  const buyerName = isShopOrder
    ? buyer.shopName || "Shop"
    : buyer.businessDetails?.businessName || buyer.basicInfo?.name || "Distributor";
  const address = isShopOrder
    ? [buyer.location?.addressLine1, buyer.location?.addressLine2, buyer.location?.city, buyer.location?.pincode]
      .filter(Boolean)
      .join(", ")
    : buyer.shopDetails
      ? [buyer.shopDetails.addressLine1, buyer.shopDetails.addressLine2, buyer.shopDetails.city, buyer.shopDetails.pincode, buyer.state?.name]
      .filter(Boolean)
      .join(", ")
    : "";
  pdf.text(buyerName, margin, y);
  y += 4;
  if (isShopOrder && buyer.ownerName) { pdf.text(buyer.ownerName, margin, y); y += 4; }
  if (!isShopOrder && buyer.basicInfo?.name) { pdf.text(buyer.basicInfo.name, margin, y); y += 4; }
  if (address) { pdf.text(pdf.splitTextToSize(address, width / 2 - margin), margin, y); y += 8; }
  if (!isShopOrder && buyer.businessDetails?.gstNumber) { pdf.text(`Buyer GSTIN: ${buyer.businessDetails.gstNumber}`, margin, y); y += 5; }
  y += 4;

  const columns = { item: margin, size: 100, qty: 128, rate: 148, amount: width - margin };
  const drawHeader = () => {
    pdf.setFillColor(242, 244, 247);
    pdf.rect(margin, y - 4, width - margin * 2, 8, "F");
    pdf.setFont("helvetica", "bold");
    pdf.text("Item", columns.item + 2, y + 1);
    pdf.text("Size", columns.size, y + 1);
    pdf.text("Qty", columns.qty, y + 1, { align: "right" });
    pdf.text("Rate", columns.rate, y + 1, { align: "right" });
    pdf.text("Amount", columns.amount - 2, y + 1, { align: "right" });
    y += 9;
    pdf.setFont("helvetica", "normal");
  };
  drawHeader();
  for (const item of order.items || []) {
    const nameLines = pdf.splitTextToSize(String(item.name || "Item"), 75);
    const rowHeight = Math.max(6, nameLines.length * 4);
    if (y + rowHeight > height - 45) {
      pdf.addPage();
      y = 18;
      drawHeader();
    }
    pdf.text(nameLines, columns.item + 2, y);
    pdf.text(String(item.size || "-"), columns.size, y);
    pdf.text(String(item.quantity || 0), columns.qty, y, { align: "right" });
    pdf.text(money(item.price).replace("INR ", ""), columns.rate, y, { align: "right" });
    pdf.text(money(item.lineTotal).replace("INR ", ""), columns.amount - 2, y, { align: "right" });
    y += rowHeight + 2;
  }
  if (y > height - 51) { pdf.addPage(); y = 20; } else y += 3;

  pdf.setDrawColor(190);
  pdf.line(width / 2, y, width - margin, y);
  y += 7;
  const rows = [
    ["Base amount", amounts.baseAmount],
    [`Discount (${amounts.discountPercent}%)`, -amounts.discountAmount],
    ["Taxable amount", amounts.taxableAmount],
    [amounts.legacy ? "GST breakdown unavailable (older order)" : `GST (${amounts.gstPercent}%)`, amounts.gstAmount],
  ];
  for (const [label, value] of rows) {
    pdf.setFont("helvetica", "normal");
    pdf.text(label, width / 2 + 2, y);
    pdf.text(`${value < 0 ? "- " : ""}${money(Math.abs(value))}`, width - margin - 2, y, { align: "right" });
    y += 6;
  }
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(12);
  pdf.text("TOTAL PAYABLE", width / 2 + 2, y + 2);
  pdf.text(money(amounts.totalAmount), width - margin - 2, y + 2, { align: "right" });
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.text("Generated by Shaha Groups.", margin, height - 12);
  pdf.save(`invoice-${order.orderNumber}.pdf`);
}
